import type { CallLabelScore, ReviewScore } from './callReviewModel';

const percent = (value: number | null): string => (value === null ? '—' : `${(value * 100).toFixed(1)}%`);

const fieldLabels: Record<keyof CallLabelScore['fields'], string> = {
    instrumentForm: 'Instrument type',
    statementKind: 'Statement',
    underlyingBias: 'Direction',
    positionSide: 'Claimed position',
    action: 'Action',
    statedLifecycle: 'Change to earlier call',
    horizonClass: 'Timeframe',
    claimedEntryPrice: 'Entry price',
    targets: 'Price levels'
};

/**
 * How the live pipeline (Luna screen, then Sonnet) scores against the answer key. The blind labels (made before the
 * pipeline's answers were shown) are the headline; the score over all labels is shown for comparison.
 */
const ScoreSummary = ({ score }: { score: ReviewScore }) => (
    <section className="space-y-4 rounded-xl border border-slate-800 bg-slate-900 p-4 text-sm">
        <ScoreBlock score={score.blind} title="Blind labels" />
        <ScoreBlock muted score={score.all} title="All labels (includes posts relabelled after seeing the pipeline)" />
    </section>
);

const ScoreBlock = ({ score, title, muted = false }: { score: CallLabelScore; title: string; muted?: boolean }) => {
    const { posts } = score;
    return (
        <div className={muted ? 'border-t border-slate-800 pt-4 opacity-80' : undefined}>
            <p className="mb-2 text-slate-300">{title}</p>
            <div className="flex flex-wrap gap-x-8 gap-y-2">
                <Fact label="Labelled posts" value={String(score.labelledPosts)} />
                <Fact label="Precision (stored calls that are real)" value={percent(score.precision)} />
                <Fact label="Recall (real calls that were stored)" value={percent(score.recall)} />
                <Fact
                    label="Calls labelled / stored / matched"
                    value={`${score.labelledCalls} / ${score.predictedCalls} / ${score.matchedCalls}`}
                />
                <Fact
                    label="Posts: right call / false alarm / missed / right no-call"
                    value={`${posts.truePositive} / ${posts.falsePositive} / ${posts.falseNegative} / ${posts.trueNegative}`}
                />
                <Fact label="Posts with calls the topic filter dropped" value={String(score.filteredPostsWithCalls)} />
            </div>
            {score.matchedCalls > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                    {(Object.keys(fieldLabels) as (keyof CallLabelScore['fields'])[]).map((field) => {
                        const { agree, compared } = score.fields[field];
                        return (
                            <span className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-300" key={field}>
                                {fieldLabels[field]}: {compared ? percent(agree / compared) : '—'}
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const Fact = ({ label, value }: { label: string; value: string }) => (
    <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-base font-semibold text-slate-100">{value}</p>
    </div>
);

export default ScoreSummary;
